import axios from 'axios';
import { ROOT_URL } from '../api/ApiConfig';

const EXPORT_TICKETS_URL = `${ROOT_URL}/ticket/export-ticket-record`;

function mapSenderToRole(sender) {
  if (sender === 'agent') return 'staff';
  if (sender === 'user') return 'customer';
  return null;
}

function mapSenderToAuthorName(sender) {
  if (sender === 'agent') return 'Staff';
  if (sender === 'user') return 'Customer';
  return sender || '';
}

function normalizeReplies(messages) {
  if (!Array.isArray(messages)) return [];

  return messages
    .map((message) => {
      const role = mapSenderToRole(message?.sender);
      const content = (message?.content || '').trim();
      if (!role || !content) return null;

      return {
        role,
        authorName: mapSenderToAuthorName(message.sender),
        content,
        createdAt: message?.createdAt || null,
      };
    })
    .filter(Boolean);
}

export function buildTicketExportRecord(record) {
  return {
    id: record?.ticketId || '',
    subject: record?.title || '',
    createdAt: record?.createdAt || null,
    replies: normalizeReplies(record?.messages),
  };
}

async function fetchTicketExportRecords(authToken) {
  const response = await axios.get(EXPORT_TICKETS_URL, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
}

export async function fetchTicketsForExport(authToken) {
  const records = await fetchTicketExportRecords(authToken);
  const tickets = records.map(buildTicketExportRecord);

  return {
    exportedAt: new Date().toISOString(),
    ticketCount: tickets.length,
    tickets,
  };
}

function escapeCsvCell(value) {
  const str = value == null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildTicketsCsv(exportData) {
  const headers = [
    'ticket_id',
    'subject',
    'ticket_created_at',
    'reply_index',
    'reply_role',
    'reply_author',
    'reply_content',
    'reply_created_at',
  ];

  const rows = [headers.join(',')];

  for (const ticket of exportData.tickets || []) {
    const base = [
      ticket.id,
      ticket.subject,
      ticket.createdAt,
    ].map(escapeCsvCell);

    if (!ticket.replies?.length) {
      rows.push(
        [...base, '', '', '', '', ''].map(escapeCsvCell).join(',')
      );
      continue;
    }

    ticket.replies.forEach((reply, replyIndex) => {
      rows.push(
        [
          ...base,
          replyIndex + 1,
          reply.role,
          reply.authorName,
          reply.content,
          reply.createdAt,
        ]
          .map(escapeCsvCell)
          .join(',')
      );
    });
  }

  return rows.join('\n');
}

function triggerFileDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function buildExportFilename(businessName, extension) {
  const stamp = new Date().toISOString().slice(0, 10);
  const prefix = (businessName || 'trustchat')
    .replace(/\s+/g, '')
    .toLowerCase() || 'trustchat';
  return `${prefix}-tickets-${stamp}.${extension}`;
}

export function downloadTicketsJson(exportData, businessName) {
  const blob = new Blob([JSON.stringify(exportData, null, 2)], {
    type: 'application/json',
  });
  triggerFileDownload(blob, buildExportFilename(businessName, 'json'));
}

export function downloadTicketsCsv(exportData, businessName) {
  const blob = new Blob([buildTicketsCsv(exportData)], {
    type: 'text/csv;charset=utf-8',
  });
  triggerFileDownload(blob, buildExportFilename(businessName, 'csv'));
}

export async function exportTicketsAs(authToken, format, businessName) {
  const exportData = await fetchTicketsForExport(authToken);
  if (format === 'csv') {
    downloadTicketsCsv(exportData, businessName);
  } else {
    downloadTicketsJson(exportData, businessName);
  }
  return exportData;
}
